/**
 * static/js/vidi/headline-engine.js
 *
 * The headline-event animation framework.
 *
 * The personal moments on the live board (event_detection.js
 * detectPersonalMoments): a big swing, a new leader, ahead of pace, slate
 * over. Renders inside the caller's target - the phone's hero, the desktop
 * scorecard - and auto-dismisses ~3s.
 *
 *   - EVENT_REGISTRY: per-event content (headline, accent).
 *   - One look: cinematic; the phone's hero gets heroTemplate.
 *
 * Eight more style families, a per-event style picker (ls_animation_style)
 * and the in-scoreboard-card event animations were retired 2026-10-11
 * (Phase 5 settings review): a picker for animations that could only show
 * on a slate of four games or fewer, in Cards view, on a desktop.
 */

(function () {
  'use strict';

  // ── Event registry ────────────────────────────────────────────────────────
  // The personal moments (event_detection.js detectPersonalMoments) - the
  // only events that render since 2026-10-11.
  const EVENT_REGISTRY = {
    'personal_big_swing':     { icon: '🔥', headline: 'BIG SWING',     accent: '#f59e0b', sport: 'personal' },
    'personal_lineup_ahead':  { icon: '📈', headline: 'AHEAD OF PACE', accent: '#10b981', sport: 'personal' },
    'personal_slate_over':    { icon: '🏁', headline: 'SLATE OVER',    accent: '#fbbf24', sport: 'personal' },
  };

  const DEFAULT_STYLE  = 'cinematic';

  // ── Per-style template renderers ──────────────────────────────────────────

  function escapeHtml(s) {
    if (s == null) return '';
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // Two decimals, like every other FPts figure in the product ("+18.0" sat
  // over a Wire card reading "+16.00"), a real minus (U+2212), and no "+" on
  // a total - a lineup's score is not a gain (run 8, TYPE-14 / DFS-15).
  function _fmtFpts(n, isTotal) {
    if (typeof n !== 'number' || !isFinite(n)) return null;
    const abs = Math.abs(n).toFixed(2);
    if (isTotal) return (n < 0 ? '−' : '') + abs;
    return (n < 0 ? '−' : '+') + abs;
  }

  function cinematicTemplate(event, data) {
    const fpts = _fmtFpts(data.fpts, !!data.fpts_total);
    const playerLine = escapeHtml(data.player_name || '');

    const stats = [];
    if (fpts) stats.push(`<div class="vh-stat"><span class="vh-stat-v">${escapeHtml(fpts)}</span><span class="vh-stat-k">FPts</span></div>`);

    return `
      <div class="vh-stack">
        <div class="vh-hairline"></div>
        ${data.team_name ? `<div class="vh-eyebrow">${escapeHtml(data.team_name)}</div>` : ''}
        <div class="vh-headline-wrap">
          <div class="vh-headline">${escapeHtml(event.headline)}</div>
        </div>
        ${playerLine ? `<div class="vh-player">${playerLine}</div>` : ''}
        ${stats.length ? `<div class="vh-stats">${stats.join('')}</div>` : ''}
      </div>
    `;
  }

  // The phone's hero, where a moment must leave the number in view. It wraps
  // around it: the headline in the label's place, the player and the points in
  // the sub-line's, and between them the hero's own number, which
  // mobile_shell.css lifts above this overlay. Every family's card layout
  // centres a four-line stack exactly where that number sits, so the moment hid
  // the one figure the phone exists to show, 3s at a time (run 8, HIER-17).
  function heroTemplate(event, data) {
    const fpts = _fmtFpts(data.fpts, !!data.fpts_total);
    const player = escapeHtml(data.player_name || '');
    // GREEN IS A GAIN, EVERYWHERE. The hero stat took its colour from the
    // event family's accent, so +18.00 rendered amber on a home run and the
    // same +18.00 two rows below it, on the Wire, rendered emerald - the one
    // convention the whole board rests on, broken on the loudest element it
    // has (run 10, HIER-27). CSS cannot read a sign, so the sign is stamped
    // here; the colours themselves are the Wire's, by token, so the two can
    // no longer drift.
    const sign = (typeof data.fpts === 'number' && isFinite(data.fpts))
      ? (data.fpts > 0 ? ' is-pos' : data.fpts < 0 ? ' is-neg' : '')
      : '';
    return `
      <div class="vh-hero-top"><span class="vh-headline">${escapeHtml(event.headline)}</span></div>
      <div class="vh-hero-bot">
        ${player ? `<span class="vh-player">${player}</span>` : ''}
        ${fpts ? `<span class="vh-stat"><span class="vh-stat-v${sign}">${escapeHtml(fpts)}</span><span class="vh-stat-k">FPts</span></span>` : ''}
      </div>
    `;
  }

  // Maps style → template fn.
  const TEMPLATES = {
    'cinematic':   cinematicTemplate,
  };

  // ── Render path ───────────────────────────────────────────────────────────

  let _activeOverlay = null;
  let _activeTimer  = null;
  let _queue        = [];
  let _burstStart   = 0;
  let _drainTimer   = null;

  // A moment nobody sees is not a moment. render() used to dismiss() on entry
  // - "cancel any in-flight overlay so they don't stack" - which is right for
  // two moments a minute apart and catastrophic for two in the same frame.
  // Simultaneity is ROUTINE here: two of your hitters going deep in the same
  // half-inning, and detectPersonalMoments fires synchronously in a loop. Three
  // qualifying deltas in one apply mounted three overlays at the same
  // millisecond and removed two of them before a single paint - 0ms visible
  // each - and the survivor was whichever came LAST in the array, so the
  // biggest swing was the one most likely lost (run 10, LIVE-20: +12 / +30 /
  // +19 fired together, and +30 was the one destroyed).
  //
  // So they queue, biggest first, and each one gets a floor of screen time.
  const MIN_DWELL = 2500;    // nothing is shown for less than this
  const MAX_QUEUE = 12000;   // ...and a burst is over within this, however
                             // many arrive mid-drain: a queue that can be
                             // extended indefinitely is a board that has
                             // stopped showing the live score.

  // THE BOARD GETS READ BEFORE ANYTHING COVERS IT. Measured signed out on the
  // MLB demo, five runs: the first overlay landed 10.13 / 10.18 / 10.26 /
  // 10.32 / 11.01s after the hero first showed a figure. A stranger who has
  // been on the page for ten seconds has not finished working out what a
  // slate, a Dream Team or "% of optimal" mean, and the single most
  // impressive thing the product does arrives on top of the thing they were
  // still reading (run 10, FIRST).
  //
  // So the FIRST overlay of a page waits. It is held, not dropped - the
  // moment still plays, and because the queue sorts by magnitude at drain
  // time, anything that piles up behind it plays biggest-first. The clock
  // starts at boardReady() rather than page load, because a slow network must
  // not spend the visitor's reading time before the board has painted.
  // 14s, not 13: the clock starts at boardReady() and the acceptance measures
  // from HERO PAINT, which lands up to ~1.2s later - at 13000 one run of five
  // came in at 11.79s against a twelve-second bar. The constant carries the
  // slack so the delivered reading time is what clears it, not the timing.
  const FIRST_GRACE = 14000;
  let _boardAt = 0;          // 0 = nobody claimed a board; no grace applies
  let _shownAny = false;

  /** The board has real games on it and the visitor can start reading.
   *  Called from the dashboard's SSE apply (dashboard/sse.js), the same place
   *  that stands the server-painted opening frame down. Idempotent. */
  function boardReady() {
    if (!_boardAt) _boardAt = Date.now();
  }

  /** ms still owed to the visitor before the first overlay may mount. */
  function _graceLeft() {
    if (_shownAny || !_boardAt) return 0;
    return Math.max(0, FIRST_GRACE - (Date.now() - _boardAt));
  }

  /**
   * Render a headline event animation as a body-level overlay.
   * Returns true if rendered, false if event isn't registered or user opted out.
   *
   * data: { player_name?, team_name?, fpts?, fpts_total? }
   * options: { target (required), duration_ms = 3200, style?, sport?,
   *            layout? ('hero' - see heroTemplate) }
   */
  /** Resolve a registry entry. Tries `${sport}_${ruleId}` first (handles
   *  cross-sport collisions like NHL `goal` vs Soccer `goal`), then bare
   *  `ruleId`. Returns [resolvedKey, eventConfig] or [null, null]. */
  function resolveEvent(ruleId, sport) {
    if (sport) {
      const k = `${sport}_${ruleId}`;
      if (EVENT_REGISTRY[k]) return [k, EVENT_REGISTRY[k]];
    }
    if (EVENT_REGISTRY[ruleId]) return [ruleId, EVENT_REGISTRY[ruleId]];
    return [null, null];
  }

  function render(eventKey, data, options) {
    options = options || {};
    data = data || {};
    const [resolvedKey, event] = resolveEvent(eventKey, data.sport || options.sport);
    if (!event) return false;
    const style = DEFAULT_STYLE;
    // layout 'hero': the phone's hero (heroTemplate), whatever the family.
    const heroLayout = options.layout === 'hero';
    const template = heroLayout ? heroTemplate : TEMPLATES[style];
    if (!template) return false;

    const duration = Math.max(options.duration_ms || 3200, MIN_DWELL);

    // Render target MUST be provided by the caller — fullscreen mode
    // was retired. If no target is passed, refuse to render rather than
    // fall back to body (which would take over the whole viewport).
    // See detectPersonalMoments in dashboard/event_detection.js.
    const target = options.target;
    if (!target) return false;

    // EVERYTHING queues, including the first - and the queue drains on the
    // next tick rather than this one. Mounting the first arrival immediately
    // meant arrival order beat magnitude for the one that matters most: three
    // fired in a synchronous loop put +12 on screen first and sorted only the
    // +30 and +19 behind it. A tick of delay costs nothing anybody can see and
    // lets the whole burst be ranked together.
    if (_queue.length >= 24) return false;
    _queue.push({event, resolvedKey, data, options, target, duration, style,
                 heroLayout, template, fpts: Number(data.fpts) || 0});
    if (!_activeOverlay && !_drainTimer) {
      _drainTimer = setTimeout(() => { _drainTimer = null; _next(); }, _graceLeft());
    }
    return true;
  }

  function _mount(job) {
    const {event, data, options, target, duration, style, heroLayout, template} = job;
    _clear();
    if (!_burstStart) _burstStart = Date.now();

    const overlay = document.createElement('div');
    // Always renders inside the caller's scorecard (.vh-inline); fullscreen
    // was retired (see the comment above), so there's no other mode.
    overlay.className = heroLayout ? 'vh-overlay vh-inline vh-hero' : `vh-overlay vh-style-${style} vh-inline`;
    overlay.style.setProperty('--vh-dur', duration + 'ms');
    overlay.dataset.tone = event.tone || 'celebration';
    overlay.dataset.sport = event.sport || '';
    overlay.style.setProperty('--vh-accent', event.accent || '#fbbf24');
    // Anchor to the card (which has a positioning context) rather than the
    // viewport — switches the fixed overlay base to parent-absolute.
    overlay.style.position = 'absolute';
    overlay.style.inset = '0';
    overlay.innerHTML = template(event, data);
    target.appendChild(overlay);

    _activeOverlay = overlay;
    _shownAny = true;          // the grace is spent; every later burst is live
    _activeTimer = setTimeout(_next, duration);
    return true;
  }

  /** Take the overlay off the screen. Does not touch the queue. */
  function _clear() {
    if (_activeTimer) { clearTimeout(_activeTimer); _activeTimer = null; }
    if (_activeOverlay && _activeOverlay.parentNode) {
      _activeOverlay.parentNode.removeChild(_activeOverlay);
    }
    _activeOverlay = null;
  }

  /** This one has had its time — show the next, biggest first. */
  function _next() {
    if (_drainTimer) { clearTimeout(_drainTimer); _drainTimer = null; }
    _clear();
    if (!_queue.length) { _burstStart = 0; return; }
    // Sorted at DRAIN time, not at push time, so a bigger moment arriving
    // while the queue is running still goes to the front of what is left.
    _queue.sort((a, b) => (b.fpts || 0) - (a.fpts || 0));
    // If there is no room left in the burst for a full dwell, the rest are
    // dropped rather than shown for a fraction of a second each.
    if (_burstStart && (Date.now() - _burstStart) + MIN_DWELL > MAX_QUEUE) {
      _queue.length = 0;
      _burstStart = 0;
      return;
    }
    _mount(_queue.shift());
  }

  /** Public cancel: the screen AND anything waiting for it. */
  function dismiss() {
    if (_drainTimer) { clearTimeout(_drainTimer); _drainTimer = null; }
    _queue.length = 0;
    _burstStart = 0;
    _clear();
  }

  // Public API
  window.VidiHeadlineEngine = {
    render,
    boardReady,
    dismiss,
    EVENT_REGISTRY,
  };
})();
